import { type ITooltipProps } from 'shared/ui'
import { type Permissions, type IPermissionsContactAccess } from 'entities/Users'

export type PermissionItemConfig = {
  key: keyof Permissions
  label: string
  valueType: 'boolean' | 'contactAccess'
  contactAccessValue?: IPermissionsContactAccess
  tooltip?: {
    label: React.ReactNode
    tooltipProps?: ITooltipProps
  }
}

export type PermissionGroupConfig = {
  key: string
  label: string
  permissions: PermissionItemConfig[]
}

export const permissionsConfig: PermissionGroupConfig[] = [
  {
    key: 'functionalityAccess',
    label: 'Functionality access',
    permissions: [
      {
        key: 'broadcasts',
        label: 'Can access Broadcasts',
        valueType: 'boolean',
      },
      {
        key: 'campaigns',
        label: 'Can access Campaigns',
        valueType: 'boolean',
      },
      {
        key: 'textBot',
        label: 'Can access AI Agent',
        valueType: 'boolean',
      },
      {
        key: 'triggers',
        label: 'Can access Triggers',
        valueType: 'boolean',
      },
      {
        key: 'reportingAndAnalytics',
        label: 'Can access Analytics',
        valueType: 'boolean',
      },
      {
        key: 'phoneChecker',
        label: 'Can access PhoneCheckr',
        valueType: 'boolean',
      },
      {
        key: 'cannedMessage',
        label: 'Can access Saved Replies',
        valueType: 'boolean',
      },
    ],
  },
  {
    key: 'contactsManagement',
    label: 'Contacts management',
    permissions: [
      {
        key: 'contactCreate',
        label: 'Can create Contacts',
        valueType: 'boolean',
      },
    ],
  },
  {
    key: 'contactsAccess',
    label: 'Contacts access',
    permissions: [
      {
        key: 'contactAccess',
        label: 'Can access all contacts',
        valueType: 'contactAccess',
        contactAccessValue: 'can_access_all',
      },
      {
        key: 'contactAccess',
        label: 'Can access contacts assigned to their team',
        valueType: 'contactAccess',
        contactAccessValue: 'can_access_groups',
      },
      {
        key: 'contactAccess',
        label: 'Can access contacts assigned to them',
        valueType: 'contactAccess',
        contactAccessValue: 'can_access_own',
      },
    ],
  },
  {
    key: 'inboxManagement',
    label: 'Inbox management',
    permissions: [
      {
        key: 'inbox',
        label: 'Can manage Inboxes',
        valueType: 'boolean',
      },
      {
        key: 'transferConversation',
        label: 'Can transfer Conversations',
        valueType: 'boolean',
      },
    ],
  },
  {
    key: 'dataAndSecurity',
    label: 'Data & Security',
    permissions: [
      {
        key: 'dataAndSecurity',
        label: 'Can manage Data & Security',
        valueType: 'boolean',
        tooltip: {
          label: (
            <>
              Can import and export contacts
              <br />
              Can manage security settings
            </>
          ),
        },
      },
      {
        key: 'billing',
        label: 'Can manage Billing',
        valueType: 'boolean',
      },
      {
        key: 'members',
        label: 'Can manage Members',
        valueType: 'boolean',
      },
      {
        key: 'manualOptIn',
        label: 'Can access Manual Opt-In',
        valueType: 'boolean',
      },
    ],
  },
]

export const PERMISSION_KEYS: (keyof Permissions)[] = [
  ...new Set(
    permissionsConfig.flatMap((group) => group.permissions.map((permission) => permission.key))
  ),
]
